<template>
	<Confirmation :title="title"
		@agree="$emit('agree', ({switchToAutoMode, syncInput}))"
		@cancel="$emit('cancel')">
		<v-card-text class="pt-6 pb-0">
			<div class="confirmation-message" v-html="message"></div>
			<div v-if="showEpisode">
				<v-row class="flex-nowrap mt-4 pb-2" align="center">
					<v-col cols="3">
						<label class="text-caption">Restart show offset:</label>
					</v-col>
					<v-col cols="3">
						<v-text-field variant="outlined" density="compact" hide-details
							type="number" v-model.lazy="syncInput.hours"
							min="0" max="23" placeholder="hh" suffix="hh"
							:readonly="joinType === 'inProgress'"/>
					</v-col>
					<v-col cols="3">
						<v-text-field variant="outlined" density="compact" hide-details
							type="number" v-model.lazy="syncInput.minutes"
							min="0" max="59" placeholder="mm" suffix="mm"
							:readonly="joinType === 'inProgress'"/>
					</v-col>
					<v-col cols="3">
						<v-text-field variant="outlined" density="compact" hide-details
							type="number" v-model.lazy="syncInput.seconds"
							min="0" max="59" placeholder="ss" suffix="ss"
							:readonly="joinType === 'inProgress'"/>
					</v-col>
				</v-row>
				<v-row v-if="error" class="mt-0">
					<v-col class="py-0">
						<p class="bg-error text-caption">{{ error }}</p>
					</v-col>
				</v-row>
				<v-row class="flex-nowrap" align="center" v-if="isCurrentEvent">
					<v-col cols="3">
						<label class="text-caption">Join:</label>
					</v-col>
					<v-col cols="9" class="pl-0 pt-0">
						<v-radio-group v-model="joinType" hide-details @update:model-value="onJoinTypeChanged">
							<v-radio
								label="From Beginning"
								value="beginning"
							></v-radio>
							<v-radio
								label="Custom"
								value="custom"
							></v-radio>
							<v-radio
								label="In progress"
								value="inProgress"
							></v-radio>
						</v-radio-group>
					</v-col>
				</v-row>
			</div>
			<v-checkbox v-model="switchToAutoMode"
				density="compact" class="pt-2"
				label="Switch back to Auto mode after complete"
				hide-details/>
		</v-card-text>
	</Confirmation>
</template>

<script>
/* Libraries */
import moment from "moment-timezone";

/* Modules */
import Confirmation from "@/components/dialogs/Confirmation.vue";

/* Plugins */
import WorkerTimer from "@/plugins/WorkerTimer";

export default {
	name: "SyncToSelectionConfirmation",

	components: {Confirmation},

	props: ["title", "message", "showEpisode", "selectedStation"],

	emits: ["agree", "cancel"],

	data() {
		return {
			switchToAutoMode: true,
			syncInput: {
				hours: 0,
				minutes: 0,
				seconds: 0
			},
			error: "",
			joinType: "inProgress"	// beginning, custom, inProgress
		}
	},

	computed: {
		duration() {
			return this.showEpisode.Asset.POIMarkup.Length;
		},

		playlistStartDateTime() {
			const playlist = this.$store.getters.getPlaylistById(
				{stationID: this.selectedStation.StationID, playlistID: this.showEpisode.PlaylistID}
			);
			return moment(playlist.ActualStartDateTime ?? playlist.ScheduledStartDateTime);
		},

		isCurrentEvent() {
			return moment.tz(this.selectedStation.StationTimeZone).isBetween(
				this.playlistStartDateTime,
				this.playlistStartDateTime.clone().add(this.duration, "seconds"),
				undefined,
				"[]"
			);
		},

		maxSyncInput() {
			return Math.min(
				this.duration,
				moment.duration(
					moment(this.showEpisode.Asset.ReleaseDateTime).clone().add(
						moment().diff(this.playlistStartDateTime, "seconds") // This is failsafe for live shows that have not yet reached the live marker
					)
				).asSeconds()
			);
		},
	},

	methods: {
		setElapsingTimeInterval() {
			this.setSyncInput();

			this.elapsingTimeInterval = WorkerTimer.setInterval(() => {
				this.setSyncInput();

				if (moment().diff(this.playlistStartDateTime, "seconds") >= this.duration) {
					this.clearElapsingTimeInterval();
				}
			}, 1000);
		},

		setSyncInput() {
			const elapsingTime = moment.duration(moment().diff(this.playlistStartDateTime, "seconds"), "seconds");

			this.syncInput = {
				hours: elapsingTime.hours(),
				minutes: elapsingTime.minutes(),
				seconds: elapsingTime.seconds()
			};
		},

		clearElapsingTimeInterval() {
			if (this.elapsingTimeInterval) {
				WorkerTimer.clearInterval(this.elapsingTimeInterval);
				this.elapsingTimeInterval = null;
			}
		},

		onJoinTypeChanged() {
			if (this.joinType === "beginning") {
				this.clearElapsingTimeInterval();

				this.syncInput = {
					hours: 0,
					minutes: 0,
					seconds: 0
				};
			}
			else if (this.joinType === "custom") {
				this.clearElapsingTimeInterval();
			}
			else {
				this.setElapsingTimeInterval();
			}
		}
	},

	mounted() {
		if (this.showEpisode) {
			if (this.isCurrentEvent) {
				this.setElapsingTimeInterval();
			}
			else {
				this.joinType = "beginning";
			}
		}
	},

	watch: {
		syncInput: {
			handler(value) {
				const syncInput = moment.duration({
					hours: value.hours, minutes: value.minutes, seconds: value.seconds
				});
				this.error = syncInput.asSeconds() > this.maxSyncInput ?
							 "Input greater than show duration" : "";
			},
			deep: true
		}
	},

	beforeUnmount() {
		if (this.showEpisode) {
			this.clearElapsingTimeInterval();
		}
	}
}
</script>

<style scoped lang="scss">
.confirmation-message {
	white-space: pre-wrap;
	text-align: center;
	margin-bottom: 15px;
}

.error {
	color: red;
}
</style>